// cSpell: ignore chartjs
import { getLoggerFor, LogAs, LogInvocation, LogLevel, LogName } from "@tylertech/logging";
import { Color, ChartConfiguration, ChartOptions, ChartType, ScaleType, Chart, TitleOptions, LegendOptions, Plugin, PluginChartOptions, TooltipOptions, CoreChartOptions, ScaleChartOptions, ScaleOptions, ScaleOptionsByType, LinearScaleOptions, LogarithmicScaleOptions, CategoryScaleOptions, TimeScaleOptions, RadialLinearScaleOptions, CartesianScaleOptions, CoreScaleOptions, ChartDataCustomTypesPerDataset, ChartDatasetCustomTypesPerDataset, ChartDatasetProperties, BarControllerDatasetOptions, ChartTypeRegistry, DefaultDataPoint, ChartData, ElementOptionsByType, ElementChartOptions, PluginOptionsByType, ChartConfigurationCustomTypesPerDataset } from "chart.js";
import { DefaultBackgroundColors, DefaultBorderColors } from "./defaultColors";
import { ChartCategory, ChartConfigValue, Series } from "./series";
import { SeriesConfig } from "./seriesConfig";
import { removeUndefinedFields } from "@tylertech/shared-module";

/**
 * I don't know why Chart.js stopped exporting this type even though they still rely on it instead of just simply making
 * all their types have options properties...
 */
export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
};

export function rgb(r: number, g: number, b: number, a?: number): Color
{
    return a
        ? `rgb(${r},${g},${b},${a})`
        : `rgb(${r},${g},${b})`;
}

export type ChartJsCanvas = HTMLCanvasElement
    & {
        $chartjs?: Chart;
    };

type ChartDataSetRegistry = { [key in ChartType]: { type: key; } & ChartTypeRegistry[key]['datasetOptions'] };

type ChartDataSetOf<TChartType extends ChartType = ChartType, TData = DefaultDataPoint<TChartType>>
    = ChartDataSetRegistry[TChartType] & ChartDatasetProperties<TChartType, TData>;

type PartialChartConfiguration<
    TType extends ChartType = ChartType,
    TData = DefaultDataPoint<TType>,
    TLabel = unknown
> = {
    type?: TType;
    data: ChartData<TType, TData, TLabel>;
    options?: ChartOptions<TType>;
    plugins?: Array<Plugin<TType>>;
};

type ChartConfigTransform<TChartType extends ChartType = ChartType, TData = unknown>
    = (x: PartialChartConfiguration<TChartType>, source: ReadonlyArray<TData>) => PartialChartConfiguration<TChartType>;

export class ChartJsConfigBuilder<TData = unknown, TChartType extends ChartType = ChartType>
{
    private readonly log = getLoggerFor("ChartJsConfigBuilder", () => `ChartJsConfigBuilder("${this.chartName}")`);

    /** For configuration transforms that cannot be data-dependent */
    private readonly preTransforms = new Map<string, ChartConfigTransform<TChartType, TData>>();
    /** For configuration transforms that define data */
    private readonly seriesTransforms = new Map<string, ChartConfigTransform<TChartType, TData>>();
    /** For configuration transforms that can be data-dependent */
    private readonly postTransforms = new Map<string, ChartConfigTransform<TChartType, TData>>();

    private backgroundColorSet: ReadonlyArray<Color> = DefaultBackgroundColors;
    private borderColorSet: ReadonlyArray<Color> = DefaultBorderColors;

    constructor(
        private readonly chartName: string,
        private readonly chartType?: TChartType)
    { }

    /** Sets the set of colors that the datasources will cycle through */
    setBackgroundColorSet(colors: ReadonlyArray<Color>): this
    {
        this.backgroundColorSet = colors;
        return this;
    }

    /** Sets the set of colors that the datasources will cycle through */
    setBorderColorSet(colors: ReadonlyArray<Color>): this
    {
        this.borderColorSet = colors;
        return this;
    }

    useChartNameAsTitle(): this
    {
        this.preTransforms.set("options.plugins.title", (c, s) =>
        {
            const pluginOptions = c.options as PluginChartOptions<TChartType>;
            if (!pluginOptions.plugins)
            {
                pluginOptions.plugins = {} as PluginOptionsByType<TChartType>;
            }

            pluginOptions.plugins.title = {
                display: !!this.chartName,
                text: this.chartName
            } as TitleOptions;

            return c;
        });
        return this;
    }

    /** Defines the title options */
    setTitle(title: DeepPartial<TitleOptions>): this
    {
        this.preTransforms.set("options.plugins.title", (c, s) =>
        {
            const pluginOptions = c.options as PluginChartOptions<TChartType>;
            if (!pluginOptions.plugins)
            {
                pluginOptions.plugins = {} as PluginOptionsByType<TChartType>;
            }

            pluginOptions.plugins.title = title as TitleOptions;

            return c;
        });
        return this;
    }

    /**
     * Defines the legend options.
     *
     * Note: use `attachStandardLegendClickHandler` to configure chart to react to click events on legend items.
     **/
    setLegend(legend: DeepPartial<LegendOptions<TChartType>>): this
    {
        this.preTransforms.set("options.plugins.legend", (c, _) =>
        {
            const pluginOptions = c.options as PluginChartOptions<TChartType>;
            if (!pluginOptions.plugins)
            {
                pluginOptions.plugins = {} as PluginOptionsByType<TChartType>;
            }

            pluginOptions.plugins.legend = legend as LegendOptions<TChartType>;

            return c;
        });
        return this;
    }

    /** Defines the tooltip options */
    setTooltips(tooltips: DeepPartial<TooltipOptions<TChartType>>): this
    {
        this.preTransforms.set("options.plugins.tooltip", (c, s) =>
        {
            const pluginOptions = c.options as PluginChartOptions<TChartType>;
            if (!pluginOptions.plugins)
            {
                pluginOptions.plugins = {} as PluginOptionsByType<TChartType>;
            }

            pluginOptions.plugins.tooltip = tooltips as TooltipOptions<TChartType>;

            return c;
        });
        return this;
    }

    onClick(handler: ChartOptions["onClick"]): this
    {
        this.preTransforms.set("options.onClick", (c, _) =>
        {
            const coreOptions = c.options as CoreChartOptions<TChartType>;
            coreOptions.onClick = handler!;

            return c;
        });
        return this;
    }

    onHover(handler: ChartOptions["onHover"]): this
    {
        this.preTransforms.set("options.onHover", (c, _) =>
        {
            const coreOptions = c.options as CoreChartOptions<TChartType>;
            coreOptions.onHover = handler!;

            return c;
        });
        return this;
    }

    /**
     * Sets the responsive field on the chart's options object
     * @see https://www.chartjs.org/docs/2.9.4/general/responsive.html
     */
    setResponsive(responsive: boolean): this
    {
        this.preTransforms.set("options.responsive", (c, s) =>
        {
            const coreOptions = c.options as CoreChartOptions<TChartType>;
            coreOptions.responsive = responsive;

            return c;
        });
        return this;
    }

    /**
     * Sets the responsive field on the chart's options object
     * @see https://www.chartjs.org/docs/2.9.4/general/responsive.html
     */
    setMaintainAspectRatio(maintainAspectRatio: boolean): this
    {
        this.preTransforms.set("options.maintainAspectRatio", (c, s) =>
        {
            const coreOptions = c.options as CoreChartOptions<TChartType>;
            coreOptions!.maintainAspectRatio = maintainAspectRatio;

            return c;
        });
        return this;
    }

    /**
     * The base axis of the chart. 'x' for vertical charts and 'y' for horizontal charts.
     * @default 'x'
     */
    setIndexAxis(indexAxis: 'x' | 'y'): this
    {
        this.preTransforms.set("options.indexAxis", (c, s) =>
        {
            const coreOptions = c.options as CoreChartOptions<TChartType>;
            coreOptions!.indexAxis = indexAxis;

            return c;
        });
        return this;
    }

    /** Defines the set of values that will be displayed along the horizontal axis */
    setCategories(categories: ReadonlyArray<ChartCategory>): this
    {
        this.postTransforms.set("data.labels", (c, s) =>
        {
            this.log.debug(`setCategories:PostTransformer`, categories);
            if (!c.data)
            {
                c.data = {
                    datasets: []
                };
            }

            if (!c.data.labels)
            {
                c.data.labels = categories as Array<ChartCategory>;
            }

            return c;
        });

        return this;
    }

    setElements(elements: DeepPartial<ElementOptionsByType<TChartType>>): this
    {
        this.preTransforms.set("options.elements", (c, s) =>
        {
            this.log.debug(`setElements:PreTransformer`, elements);
            const elementOptions = c.options as ElementChartOptions<TChartType>;
            elementOptions.elements = elements as ElementOptionsByType<TChartType>;

            return c;
        });

        return this;
    }

    setDatasets(datasets: DeepPartial<CoreChartOptions<ChartType>["datasets"]>): this
    {
        this.preTransforms.set("options.datasets", (c, s) =>
        {
            this.log.debug(`setDatasets:PreTransformer`, datasets);
            const chartOptions = c.options as CoreChartOptions<TChartType>;
            chartOptions.datasets = datasets as CoreChartOptions<ChartType>["datasets"];

            return c;
        });

        return this;
    }

    addScales<T extends ScaleType = ScaleType>(scales: Record<string, DeepPartial<ScaleOptionsByType<T>>>): this;
    addScales<T extends ScaleType = ScaleType>(scales: Array<[string, DeepPartial<ScaleOptionsByType<T>>]>): this;
    addScales<T extends ScaleType = ScaleType>(objectOrArray: Record<string, DeepPartial<ScaleOptionsByType<T>>> | Array<[string, DeepPartial<ScaleOptionsByType<T>>]>): this
    {
        const entries = Array.isArray(objectOrArray)
            ? objectOrArray
            : Object.entries(objectOrArray);

        entries.forEach(([id, scale]: [string, DeepPartial<ScaleOptionsByType<T>>]) =>
        {
            this.addScale(id, scale);
        });

        return this;
    }

    @LogInvocation(LogLevel.DEBUG)
    addScale<T extends ScaleType>(
        id: string,
        @LogAs((x: ScaleOptionsByType) => `Scale{"${x.type}"}`) scale: DeepPartial<ScaleOptionsByType<T>>): this
    {
        this.preTransforms.set(`options.scales["${id}"]`, (c, s) =>
        {
            const scaleOptions = c.options as ScaleChartOptions<TChartType>;
            if (!scaleOptions.scales)
            {
                scaleOptions.scales = {};
            }

            scaleOptions.scales[id] = scale as ScaleOptionsByType<T>;

            return c;
        });

        return this;
    }

    /**
     * Adds all of the given data series to the chart.
     */
    addSeries<T extends ChartType = TChartType>(series: Array<Series<TData>>): this;
    /**
     * Adds a single data series to the chart.
     */
    addSeries<T extends ChartType = TChartType>(series: Series<TData>): this;
    @LogInvocation(LogLevel.DEBUG)
    addSeries<T extends ChartType = TChartType>(
        @LogAs((x: SeriesConfig<TData>) => Array.isArray(x) ? `Array<Series>` : `Series{"${x.name}"}`)
        seriesOrArray: Array<Series<TData>> | Series<TData>): this
    {
        const set = Array.isArray(seriesOrArray) ? seriesOrArray : [seriesOrArray];

        set.forEach(series =>
        {
            const index = this.seriesTransforms.size;
            const seriesKey = `data.datasets[${index}]`;
            this.seriesTransforms.set(seriesKey, (chartConfig, source) =>
            {
                this.log.debug(`addSeries:Transformer | ${seriesKey} | series: "${series.name}"`, series);
                const scaleChartOptions = chartConfig.options! as ScaleChartOptions<T>;
                if (!scaleChartOptions.scales)
                {
                    scaleChartOptions.scales = {};
                }

                const seriesConfig = new SeriesConfig(series as Series<TData>, index, source);


                if (!["doughnut", "pie", "polarArea", "radar"].includes(series.type))
                {
                    this.createHorizontalAxis(scaleChartOptions, seriesConfig);
                    this.createVerticalAxis(scaleChartOptions, seriesConfig);
                }

                const seriesDataSet = removeUndefinedFields(
                    this.createDataset(chartConfig, seriesConfig, source));
                const data = chartConfig.data as ChartDataCustomTypesPerDataset;
                data!.datasets!.push(seriesDataSet as ChartDatasetCustomTypesPerDataset);

                return chartConfig;
            });
        });

        return this;
    }

    private createHorizontalAxis<T extends ChartType = TChartType>(
        scaleChartOptions: ScaleChartOptions<T>,
        series: SeriesConfig<TData, T>): void
    {
        if (series.xAxisId)
        {
            const existingById = scaleChartOptions.scales[series.xAxisId];
            if (existingById)
            {
                return;
            }

            this.log.warning(`createHorizontalAxis | x axis '${series.xAxisId}' does not exist`, series);
        }

        const scaleType = this.getScaleType(series.type, true);
        const existing = Object.entries(scaleChartOptions.scales)
            .find(([, scale]) => scale.type === scaleType);
        if (existing)
        {
            this.log.debug(`createHorizontalAxis | x axis for type already exists`, series);
            return;
        }

        const scales = scaleChartOptions.scales as { [name: string]: ScaleOptions; };
        const id = `x${series.index}`;

        this.log.debug(`createHorizontalAxis | creating new x axis for series`, { id, series });
        scales[id] = {
            type: scaleType,
            display: true,
            position: "bottom",
            beginAtZero: true,
            labels: series.labels as Array<string>,
        };
    }

    private createVerticalAxis<T extends ChartType = TChartType>(
        scaleChartOptions: ScaleChartOptions<T>,
        series: SeriesConfig<TData, T>): void
    {
        if (series.yAxisId)
        {
            const existingById = scaleChartOptions.scales[series.yAxisId];
            if (existingById)
            {
                return;
            }

            this.log.warning(`createVerticalAxis | y axis '${series.yAxisId}' does not exist`, series);
        }

        const scaleType = this.getScaleType(series.type);
        const existing = Object.entries(scaleChartOptions.scales)
            .find(([_, scale]) => scale.type === scaleType);
        if (existing)
        {
            this.log.debug(`createVerticalAxis | y axis for type already exists`, series);
            // series.yAxisId = existing[0];
            return;
        }

        const scales = scaleChartOptions.scales as { [name: string]: ScaleOptions; };
        const id = `y${series.index}`;
        // series.yAxisId = id;

        this.log.debug(`createVerticalAxis | creating new y axis for series`, { id, series });
        scales[id] = {
            type: scaleType,
            display: true,
            position: "left",
            beginAtZero: true,
        };
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private createDataset(
        @LogName("chart") chart: PartialChartConfiguration<TChartType>,
        @LogAs((x: SeriesConfig<TData>) => `Series{"${x.name}"}`) series: SeriesConfig<TData>,
        @LogName("source") source: ReadonlyArray<TData>): ChartDatasetCustomTypesPerDataset
    {
        switch (series.type)
        {
            case "bar":
                return this.createBarDataset(chart, series as SeriesConfig<TData, "bar">, source);
            case "line":
                return this.createLineDataset(chart, series as SeriesConfig<TData, "line">, source);
            case "pie":
                return this.createPieDataset(chart, series as SeriesConfig<TData, "pie">, source);
            case "doughnut":
                return this.createDoughnutDataset(chart, series as SeriesConfig<TData, "doughnut">, source);
            default:
                throw new Error(`ChartDataSetTypeError: unsupported data set type; was ${series.type}`);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    private createLineDataset(
        @LogName("chart") chart: PartialChartConfiguration<TChartType>,
        @LogAs((x: SeriesConfig<TData>) => `Series{"${x.name}"}`) series: SeriesConfig<TData, "line">,
        @LogName("source") source: ReadonlyArray<TData>): ChartDataSetOf<"line">
    {
        const scaleOptions = chart.options as ScaleChartOptions;
        return {
            type: "line",
            data: this.transformData(series, source),
            label: series.name,
            fill: series.get("fill") ?? false,

            xAxisID: this.findAxesId(scaleOptions.scales, series.xAxisId, this.getScaleType(series.type, true)),
            yAxisID: this.findAxesId(scaleOptions.scales, series.yAxisId, this.getScaleType(series.type)),

            backgroundColor: series.get("backgroundColor") as ChartConfigValue<Color>
                ?? this.backgroundColorSet[series.index % this.backgroundColorSet.length],
            borderColor: series.get("border")?.color as ChartConfigValue<Color>
                ?? this.borderColorSet[series.index % this.borderColorSet.length],

            pointStyle: series.get("point")?.style ?? "circle",
            pointBorderColor: series.get("point")?.borderColor
                ?? this.borderColorSet[series.index % this.borderColorSet.length],
            pointBackgroundColor: series.get("point")?.backgroundColor
                ?? this.backgroundColorSet[series.index % this.backgroundColorSet.length],

            pointHoverBackgroundColor: series.get("point")?.hoverBackgroundColor,
            pointHoverBorderColor: series.get("point")?.hoverBorderColor,
        } as ChartDataSetOf<"line">;
    }

    @LogInvocation(LogLevel.DEBUG)
    private createBarDataset(
        @LogName("chart") chart: PartialChartConfiguration<TChartType>,
        @LogAs((x: SeriesConfig<TData>) => `Series{"${x.name}"}`) series: SeriesConfig<TData, "bar">,
        @LogName("source") source: ReadonlyArray<TData>): ChartDataSetOf<"bar">
    {
        const scaleOptions = chart.options as ScaleChartOptions;
        return {
            type: "bar",
            data: this.transformData<number | [number, number] | null>(series, source),
            label: series.name,

            xAxisID: this.findAxesId(scaleOptions.scales, series.xAxisId, this.getScaleType(series.type, true)),
            yAxisID: this.findAxesId(scaleOptions.scales, series.yAxisId, this.getScaleType(series.type)),

            backgroundColor: series.get("backgroundColor") as ChartConfigValue<Color>
                ?? this.backgroundColorSet[series.index % this.backgroundColorSet.length],
            borderColor: series.get("border")?.color as ChartConfigValue<Color>
                ?? this.borderColorSet[series.index % this.borderColorSet.length],

            barPercentage: series.get("barPercentage"),
            categoryPercentage: series.get("categoryPercentage"),
        } as ChartDataSetOf<"bar">;
    }

    @LogInvocation(LogLevel.DEBUG)
    private createPieDataset(
        @LogName("chart") _chart: PartialChartConfiguration<TChartType>,
        @LogAs((x: SeriesConfig<TData>) => `Series{"${x.name}"}`) series: SeriesConfig<TData, "pie">,
        @LogName("source") source: ReadonlyArray<TData>): ChartDataSetOf<"pie">
    {
        return {
            type: "pie",
            data: this.transformData(series, source),
            label: series.name,
            backgroundColor: series.get("backgroundColor") as ChartConfigValue<Color>
                ?? this.backgroundColorSet,
            borderColor: series.get("border")?.color as ChartConfigValue<Color>
                ?? this.borderColorSet,
        } as ChartDataSetOf<"pie">;
    }

    @LogInvocation(LogLevel.DEBUG)
    private createDoughnutDataset(
        @LogName("chart") chart: PartialChartConfiguration<TChartType>,
        @LogAs((x: SeriesConfig<TData>) => `Series{"${x.name}"}`) series: SeriesConfig<TData, "doughnut">,
        @LogName("source") source: ReadonlyArray<TData>): ChartDataSetOf<"doughnut">
    {
        return {
            type: "doughnut",
            data: this.transformData(series, source),
            label: series.name,
            circumference: series.get("circumference") as ChartConfigValue<number>,
            backgroundColor: series.get("backgroundColor") as ChartConfigValue<Color>
                ?? this.backgroundColorSet[series.index % this.backgroundColorSet.length],
            borderColor: series.get("border")?.color as ChartConfigValue<Color>
                ?? this.borderColorSet[series.index % this.borderColorSet.length],
        } as ChartDataSetOf<"doughnut">;
    }

    private transformData<T>(series: SeriesConfig<TData>, source: ReadonlyArray<TData>): Array<T>
    {
        return source.map((x, i) => series.getValue(x, i)) as Array<T>;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private findAxesId<TChartType extends ChartType = ChartType>(
        @LogName("scales") scales: ScaleChartOptions<TChartType>["scales"],
        axisId: string | undefined,
        scaleType: ScaleType,
        guess?: boolean): string | undefined
    {
        if (!Object.values(scales).length)
        {
            this.log.debug(`No axis has been pre-defined`);
            return undefined;
        }

        if (axisId)
        {
            const tuple = Object.entries(scales)
                .find(([id, _x]) => id === axisId);
            if (!tuple)
            {
                this.log.debug(`AxisId has not been defined...`);
            }
            return axisId;
        }

        if (guess)
        {
            this.log.debug(`AxisId was not set; making best(?) guess`);
            const tuple = Object.entries(scales)
                .find(([_, x]) => x.type === scaleType);
            return tuple
                ? tuple[0]
                : undefined;
        }

        return undefined;

    }

    private getScaleType(
        type: ChartType | undefined,
        isX?: boolean): ScaleType
    {
        switch (type)
        {
            case "radar":
            case "polarArea":
                return "radialLinear";
            case "bar":
                return "category";
            case "line":
                return "linear";
            default:
                return isX
                    ? "category"
                    : "linear";
        }
    }

    @LogInvocation(LogLevel.DEBUG, true)
    build(source: ReadonlyArray<TData>): ChartConfiguration<TChartType> | ChartConfigurationCustomTypesPerDataset
    {
        const preDataSeries = [...this.preTransforms.values()];
        const series = [...this.seriesTransforms.values()];
        const postDataSeries = [...this.postTransforms.values()];
        // TODO- should we have any kind of validation first?

        const transforms: ReadonlyArray<ChartConfigTransform<TChartType, TData>> = [
            ...preDataSeries,
            ...series,
            ...postDataSeries,
            (chartConfig, _) =>
            {
                if (chartConfig.options?.animation === undefined)
                {
                    this.log.debug(`build | disabling animations`);
                    chartConfig.options.animation = false;
                }

                return chartConfig;
            },
            (chartConfig, _) =>
            {
                if (!chartConfig.data?.datasets)
                {
                    return chartConfig;
                }

                const labeledScale = chartConfig.options?.scales
                    ? Object.entries(chartConfig.options.scales)
                        .find(([id, scale]) =>
                        {
                            return (scale as CategoryScaleOptions).labels?.length;
                        })
                    : undefined;

                // Note: making sure that we always have labels... they are apparently very important
                if (!labeledScale
                    && !chartConfig.data!.labels)
                {
                    this.log.debug(`build | Making up some labels since you forgot to label anything`);
                    const largestDataset = Math.max(...chartConfig.data.datasets.map(x => x.data?.length ?? 0));
                    chartConfig.data!.labels = enumerate(largestDataset);
                }

                return chartConfig;
            },
        ];
        return transforms.reduce(
            (chartConfig, transform, index: number) =>
            {
                return transform(chartConfig, source);
            },
            this.createEmptyConfig()) as ChartConfiguration<TChartType>;
    }

    private createEmptyConfig(): PartialChartConfiguration<TChartType>
    {
        return removeUndefinedFields({
            type: this.chartType!,
            data: {
                datasets: [],
            },
            options: {
                maintainAspectRatio: false,
                layout: {
                    autoPadding: true,
                    padding: 16, // in Pixels
                },
            } as ChartOptions<TChartType>,
        });
    }
}

/**
 * Creates an array with all the numbers from 0 to the given number...
 */
function enumerate(range: number): Array<number>
{
    const a: Array<number> = [];
    for (let i = 0; i < range; i++)
    {
        a.push(i);
    }
    return a;
}
